/**
 *
 * Styles for OfferCard
 *
 */

import styled from "styled-components";
import { breakpoints, grid } from "root/libs/ui-styleguide/src";
import { DhlText } from "@dhl-official/react-library";
import { Accordion, IconButtonTooltip } from "root/libs/ui-components/src";
import QuoteValidity from "one-time-shipment/src/components/Offers/QuoteValidity";

export const StyledOfferCard = styled.div`
  position: relative;
  width: 100%;
  background-color: var(--dui-color-white-500);
  box-shadow: rgb(229, 229, 229) 0 var(--gaq-size-space-3px) var(--dui-size-space-4x) 0;
  transition: ease-in-out 200ms box-shadow;
  border-radius: var(--dui-size-radius-md);

  > .accordion {
    width: 100%;
    margin: 0 0 var(--gaq-size-space-30px) 0;
    padding: 0;
  }

  .offerCollapsibleContent {
    margin-bottom: 0;
    padding: 0;

    &.expanded {
      padding: 2.1rem 2.1rem var(--gaq-size-space-30px) 2.1rem;
    }
  }

  &:hover {
    box-shadow: rgba(80, 80, 80, 0.7) 0 var(--gaq-size-space-3px) var(--dui-size-space-4x) 0;

    .accordion {
      & > div:not(.accordionPanel) {
        button {
          box-shadow: rgba(80, 80, 80, 0.7) 0px 8px 5px -5px;
        }
      }
    }
  }

  &:focus,
  &:focus-within {
    box-shadow: 0 var(--dui-size-space-1x) var(--gaq-size-space-7px) var(--dui-size-space-1) var(--dui-color-gray-500);
    outline: 2px solid transparent;

    .accordion {
      & > div:not(.accordionPanel) {
        button {
          box-shadow: 0 var(--dui-size-space-4x) var(--gaq-size-space-5px) var(--gaq-size-space--5px) var(--dui-color-gray-500);
        }
      }
    }
  }

  @media print {
    padding: var(--dui-size-space-10x)
    page-break-after: always;
    page-break-inside: avoid;
    margin-top: var(--gaq-size-space-30px);

    & .accordion > dd {
      height: auto;
    }
  }
`;

const CardContentPaddingMobile = "20px 10px 30px";
const CardContentPaddingTablet = "30px 20px";
const CardContentPaddingDesktop = "25px 30px";

export const OfferCardContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 190px;
  padding: ${CardContentPaddingMobile};

  @media screen and (min-width: ${breakpoints.tablet}) {
    padding: ${CardContentPaddingTablet};
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0;
  }

  @media screen and (min-width: ${breakpoints.desktop}) {
    padding: ${CardContentPaddingDesktop};
  }
`;

export const DeliveryInfoContent = styled.div`
  margin-bottom: var(--gaq-size-space-30px);

  @media screen and (min-width: ${breakpoints.tablet}) {
    flex-basis: 48rem;
  }

  @media screen and (min-width: ${breakpoints.desktop}) {
    margin-bottom: 0;
    flex-basis: 58rem;
  }

  @media all and (-ms-high-contrast: none) {
    width: 66%;
  }
`;

export const PaymentInfoContent = styled.div`
  display: block;

  @media screen and (min-width: ${breakpoints.tablet}) {
    min-width: 280px;
  }

  @media screen and (min-width: ${breakpoints.desktop}) {
    width: 280px;
  }
`;
export const StyledAccordion = styled(Accordion)`
  position: relative;
  padding: 0;

  .accordion {
    width: 100%;

    & > div > button:focus {
      outline: 1px dotted var(--dui-color-gray-700);
    }

    & > div:not(.accordionPanel) {
      button {
        position: absolute;
        z-index: 1;
        bottom: -20px;
        background: #fff;
        box-shadow: rgb(229 229 229) 0 var(--gaq-size-space-9px)
          var(--dui-size-space-3x) var(--gaq-size-space--5px);
        transition: ease-in-out 200ms box-shadow;
      }
    }
  }
`;

export const OfferCardDetailsContent = styled.div`
  padding-top: var(--gaq-size-space-30px);
  padding-bottom: 0;
  margin-left: var(--dui-size-space-5x);
  margin-right: var(--dui-size-space-5x);
  border-top: var(--dui-size-width-border-xs) solid var(--dui-color-gray-300);
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export const DeliveryInfoWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  > .icon {
    order: 2;
    height: 100%;
    width: 100%;
    text-align: right;
    max-width: 72px;
    align-self: flex-end;
    margin-bottom: auto;
  }

  @media screen and (min-width: ${breakpoints.tablet}) {
    flex-direction: row;
    align-items: center;

    > .icon {
      margin-right: var(--gaq-size-space-30px);
      margin-bottom: 0;
      order: 1;
      text-align: left;
      height: auto;
      width: auto;
      max-width: unset;
      align-self: unset;
    }
  }
`;

export const StyledEstimatedDeliveryText = styled(DhlText)`
  & > *:is(p, span) {
    display: block;
    color: var(--dui-color-black-500);
    margin: 0;
    margin-bottom: var(--dui-size-space-1x);
  }
`;

export const StyledEstimatedDeliveryInfo = styled(DhlText)`
  & > *:is(p, span) {
    margin-top: 0px;
  }
`;

export const StyledTextAndTooltip = styled.div`
  display: flex;
  margin-bottom: var(--dui-size-space-10x);
  align-items: center;
  padding: 0 var(--dui-size-space-5x);
  position: relative;

  /* Hide tooltip button when accordion is closed */
  [role="button"] {
    ${(props) =>
      !props.isAccordionOpen &&
      `
      pointer-events: none;
      visibility: hidden;
    `}
  }
`;

export const StyledIconButtonTooltip = styled(IconButtonTooltip)`
  position: absolute;
  right: 0;
  top: 0;
`;

export const StyledPriceContainer = styled.div`
  display: block;

  @media screen and (min-width: ${breakpoints.tablet}) {
    min-width: 280px;
  }

  @media screen and (min-width: ${breakpoints.desktop}) {
    width: 280px;
  }
`;

export const StyledContainer = styled.div`
  padding: var(--dui-size-space-5x) var(--dui-size-space-10x)
    var(--dui-size-space-10x) var(--dui-size-space-10x);
`;

export const StyledCTAWrapper = styled.div`
  display: flex;
  @media screen and (min-width: ${breakpoints.tablet}) {
    padding-right: var(--gaq-size-space-50px);
  }

  @media screen and (min-width: ${breakpoints.desktop}) {
    padding-right: 0;
    align-items: inherit;

    .icon {
      margin-top: var(--gaq-size-space-5px);
    }
  }
`;

export const StyledAdditionalOfferDetails = styled.div`
  padding-bottom: 0;
  margin-left: var(--dui-size-space-5x);
  margin-right: var(--dui-size-space-5x);

  @media screen and (min-width: ${breakpoints.tablet}) {
    padding-bottom: 0;
    margin-left: var(--dui-size-space-10x);
    margin-right: var(--dui-size-space-10x);
  }

  dhl-text > span {
    font-size: 1.5rem !important;
  }
`;

export const ContentWrapper = styled.div`
  order: 1;
  width: 100%;

  @media screen and (min-width: ${breakpoints.tablet}) {
    order: 2;
    padding-right: var(--gaq-size-space-30px);
  }
`;

export const OfferWrapper = styled.div`
  margin-bottom: var(--gaq-size-space-50px);

  @media screen and (min-width: ${breakpoints.tablet}) {
    margin-bottom: var(--gaq-size-space-45px);
    padding: 0 ${grid.gutter.m};
  }

  @media screen and (min-width: ${breakpoints.desktop}) {
    margin-bottom: var(--gaq-size-space-35px);
    padding: 0 0;
    max-width: ${grid.container.maxWidthInnerContainer}px;
  }
`;

export const StyledQuoteValidity = styled(QuoteValidity)`
  @media screen and (min-width: ${breakpoints.tablet}) {
    margin-left: var(--gaq-size-space-30px);
  }
`;
